<template>
  <header class="bg-surface shadow-sm border-b border-border theme-transition">
    <div class="px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <!-- Left side -->
        <div class="flex items-center">
          <!-- Mobile menu button -->
          <button
            @click="$emit('toggle-sidebar')"
            class="lg:hidden p-2 rounded-md text-text-muted hover:text-text-primary hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/20"
          >
            <Bars3Icon class="h-6 w-6" />
          </button>
          
          <!-- Search bar -->
          <div class="hidden sm:block ml-4 lg:ml-8">
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon class="h-5 w-5 text-text-muted" />
              </div>
              <input
                v-model="searchQuery"
                @input="handleSearch"
                type="text"
                placeholder="Search..."
                class="block w-full pl-10 pr-3 py-2 border border-border rounded-lg leading-5 bg-surface text-text-primary placeholder-text-muted focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm theme-transition"
              />
            </div>
          </div>
        </div>

        <!-- Right side -->
        <div class="flex items-center space-x-4">
          <!-- Theme Toggle -->
          <button
            @click="themeStore.toggleTheme()"
            class="p-2 rounded-lg text-text-secondary hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-primary/20 theme-transition"
            aria-label="Toggle theme"
          >
            <SunIcon v-if="themeStore.isDark" class="h-5 w-5" />
            <MoonIcon v-else class="h-5 w-5" />
          </button>

          <!-- Notifications -->
          <div class="relative">
            <button
              @click="notificationsOpen = !notificationsOpen"
              class="p-2 rounded-lg text-text-secondary hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-primary/20 theme-transition"
              aria-label="notifications"
            >
              <BellIcon class="h-6 w-6" />
              <span 
                v-if="unreadNotifications > 0"
                class="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-danger rounded-full"
              >
                {{ unreadNotifications }}
              </span>
            </button>
            
            <!-- Notifications dropdown -->
            <div
              v-if="notificationsOpen"
              class="absolute right-0 mt-2 w-80 bg-surface rounded-lg shadow-large border border-border z-50 notifications-dropdown"
            >
              <div class="py-2">
                <div class="px-4 py-2 border-b border-border">
                  <h3 class="text-sm font-semibold text-text-primary">Notifications</h3>
                </div>
                <div class="max-h-96 overflow-y-auto scrollbar-custom">
                  <div v-if="notifications.length === 0" class="px-4 py-8 text-center text-text-muted">
                    <BellSlashIcon class="mx-auto h-12 w-12 opacity-50" />
                    <h3 class="mt-2 text-sm font-medium">No notifications</h3>
                  </div>
                  <div v-else>
                    <div
                      v-for="notification in notifications"
                      :key="notification.id"
                      class="px-4 py-3 hover:bg-surface-hover cursor-pointer"
                      @click="markAsRead(notification.id)"
                    >
                      <div class="flex items-start">
                        <div class="flex-shrink-0">
                          <div 
                            class="w-2 h-2 rounded-full"
                            :class="notification.status === 'unread' ? 'bg-primary' : 'bg-border'"
                          ></div>
                        </div>
                        <div class="ml-3 flex-1">
                          <p class="text-sm font-medium text-text-primary">
                            {{ notification.message }}
                          </p>
                          <p class="text-xs text-text-muted mt-1">
                            {{ formatTime(notification.createdAt) }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="px-4 py-2 border-t border-border">
                  <router-link
                    to="/notifications"
                    class="text-sm text-primary font-bold hover:underline"
                  >
                    View all notifications
                  </router-link>
                </div>
              </div>
            </div>
          </div>

          <!-- User menu -->
          <div class="relative">
            <button
              @click="userMenuOpen = !userMenuOpen"
              class="flex items-center space-x-3 p-1.5 pr-3 rounded-lg text-text-secondary hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-primary/20 theme-transition"
              aria-label="user-menu"
            >
              <div class="flex-shrink-0">
                <div class="w-8 h-8 bg-gradient-to-br from-primary to-primary-700 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-800">
                  <span class="text-xs font-bold text-[#1c2129]">
                    {{ userInitials }}
                  </span>
                </div>
              </div>
              <div class="hidden md:block text-left">
                <p class="text-xs font-bold text-text-primary">{{ authStore.userName || 'User' }}</p>
                <p class="text-[10px] text-text-muted uppercase tracking-wider">{{ authStore.userRole || 'user' }}</p>
              </div>
              <ChevronDownIcon class="h-4 w-4 text-text-muted" />
            </button>
            
            <!-- User dropdown -->
            <div
              v-if="userMenuOpen"
              class="absolute right-0 mt-2 w-48 bg-surface rounded-lg shadow-large border border-border z-50 user-menu-dropdown"
            >
              <div class="py-1">
                <router-link
                  to="/app/profile"
                  class="flex items-center px-4 py-2 text-sm text-text-primary hover:bg-surface-hover"
                  @click="userMenuOpen = false"
                >
                  <UserIcon class="mr-3 h-5 w-5 text-text-muted" />
                  Profile
                </router-link>
                <router-link
                  to="/settings"
                  class="flex items-center px-4 py-2 text-sm text-text-primary hover:bg-surface-hover"
                  @click="userMenuOpen = false"
                >
                  <Cog6ToothIcon class="mr-3 h-5 w-5 text-text-muted" />
                  Settings
                </router-link>
                <div class="border-t border-border mt-1 pt-1"></div>
                <button
                  @click="handleLogout"
                  class="flex items-center w-full px-4 py-2 text-sm text-danger hover:bg-danger/5"
                >
                  <ArrowRightOnRectangleIcon class="mr-3 h-5 w-5 opacity-70" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../../store/auth'
import { useThemeStore } from '../../store/theme'
import { useToast } from 'vue-toastification'
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  BellIcon,
  BellSlashIcon,
  UserIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  SunIcon,
  MoonIcon
} from '@heroicons/vue/24/outline'

// Props
const props = defineProps({
  searchPlaceholder: {
    type: String,
    default: 'Search...'
  }
})

// Emits
const emit = defineEmits(['toggle-sidebar', 'logout', 'search'])

// Composables
const router = useRouter()
const authStore = useAuthStore()
const themeStore = useThemeStore()
const toast = useToast()

// State
const searchQuery = ref('')
const notificationsOpen = ref(false)
const userMenuOpen = ref(false)
const notifications = ref([])
const unreadNotifications = ref(0)

// Computed
const userInitials = computed(() => {
  if (!authStore.user) return 'U'
  
  const firstName = authStore.user.firstName || ''
  const lastName = authStore.user.lastName || ''
  
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  } else if (firstName) {
    return firstName.charAt(0).toUpperCase()
  } else if (lastName) {
    return lastName.charAt(0).toUpperCase()
  } else if (authStore.user.email) {
    return authStore.user.email.charAt(0).toUpperCase()
  } else {
    return 'U'
  }
})

// Methods
const handleSearch = () => {
  emit('search', searchQuery.value)
}

const handleLogout = async () => {
  try {
    await authStore.logout()
    router.push('/login')
    toast.success('Logged out successfully')
  } catch (error) {
    toast.error('Logout failed')
  }
}

const markAsRead = async (notificationId) => {
  try {
    // Mark notification as read
    const notification = notifications.value.find(n => n.id === notificationId)
    if (notification && notification.status === 'unread') {
      notification.status = 'read'
      unreadNotifications.value--
    }
  } catch (error) {
    console.error('Error marking notification as read:', error)
  }
}

const formatTime = (timestamp) => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffInMinutes = Math.floor((now - date) / (1000 * 60))
  
  if (diffInMinutes < 1) return 'Just now'
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`
  return date.toLocaleDateString()
}

const fetchNotifications = async () => {
  try {
    // Mock notifications for now
    notifications.value = [
      {
        id: 1,
        message: 'New transaction completed',
        status: 'unread',
        createdAt: new Date(Date.now() - 5 * 60 * 1000)
      },
      {
        id: 2,
        message: 'System maintenance scheduled',
        status: 'read',
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000)
      }
    ]
    
    unreadNotifications.value = notifications.value.filter(n => n.status === 'unread').length
  } catch (error) {
    console.error('Error fetching notifications:', error)
  }
}

// Click outside handlers
const handleClickOutside = (event) => {
  const notificationsDropdown = event.target.closest('.notifications-dropdown')
  const userMenuDropdown = event.target.closest('.user-menu-dropdown')
  const notificationsButton = event.target.closest('button[aria-label="notifications"]')
  const userMenuButton = event.target.closest('button[aria-label="user-menu"]')
  
  if (!notificationsDropdown && !notificationsButton) {
    notificationsOpen.value = false
  }
  if (!userMenuDropdown && !userMenuButton) {
    userMenuOpen.value = false
  }
}

// Lifecycle
onMounted(() => {
  fetchNotifications()
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* Smooth transitions */
.transition-all {
  transition: all 0.2s ease-in-out;
}

.notifications-dropdown,
.user-menu-dropdown {
  animation: scale-in 0.2s ease-out;
}

/* Dropdown animations */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Notification badge animation */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.bg-red-500 {
  animation: pulse 2s infinite;
}

/* Search input focus animation */
.focus\:ring-1:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Hover effects */
.hover\:bg-gray-100:hover {
  transition: background-color 0.2s ease;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .hidden {
    display: none;
  }
}
</style>
